import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  InyovaGrowFinancialDetail,
  InyovaGrowPercentages,
  InyovaGrowRiskGroups,
  InyovaGrowSurveyQuestion,
  GrowRiskFormComponent,
  GrowProject,
  GrowInvestmentAmountComponent
} from '@inyova/grow';
import { AmplitudeService } from '@inyova/inyova-shared';
import { GrowAccount } from '@inyova/models';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as fromGrow from '../../grow.reducers';
import * as fromAppRoot from '@app/app.reducers';

import { LanguageService } from '@shared/services/language.service';

import * as GrowActions from '../../grow-actions';
import { GrowService } from '../../services/grow.service';

@Component({
  selector: 'app-grow-risk',
  templateUrl: './grow-risk.component.html',
  styleUrls: ['./grow-risk.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GrowRiskComponent implements OnInit, OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  growAccount: GrowAccount;
  riskLoaded$: Observable<boolean>;
  checkLoading$: Observable<boolean>;
  submitLoading$: Observable<boolean>;
  checkOrSubmitLoading$: Observable<boolean>;
  investmentPlan: { initial_investment: number; monthly_investment: number };
  riskPercentages: InyovaGrowPercentages;
  growRisk: { surveyGroups: InyovaGrowRiskGroups; financialDetail: InyovaGrowFinancialDetail };
  accountID: string;
  step = 1;
  hasSelectedProject$: Observable<boolean>;
  selectedProjects: GrowProject[];
  infoList = {
    projectsInfo: false
  };

  @ViewChild(GrowInvestmentAmountComponent) amountFormComponent!: GrowInvestmentAmountComponent;
  @ViewChild(GrowRiskFormComponent) formComponent!: GrowRiskFormComponent;

  constructor(
    private store: Store,
    private router: Router,
    private growService: GrowService,
    private amplitudeService: AmplitudeService,
    private changeDetectorRef: ChangeDetectorRef,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.languageService.selected.pipe(takeUntil(this.onDestroy$)).subscribe((language) => {
      this.store.dispatch(GrowActions.getGrowRisk({ language }));
    });

    this.store
      .select(fromAppRoot.selectCustomerAndGrowAccount)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ customer, growAccount }) => {
        if (!growAccount) {
          return;
        }
        this.router.navigate([this.growService.getTargetUrlBasedOnStepStatus(growAccount, customer)]);
      });

    this.riskLoaded$ = this.store.select(fromGrow.selectGrowRiskLoaded);
    this.checkLoading$ = this.store.select(fromGrow.selectRiskCheckLoading);
    this.submitLoading$ = this.store.select(fromGrow.selectRiskSubmitLoading);

    this.checkOrSubmitLoading$ = combineLatest([this.submitLoading$, this.checkLoading$]).pipe(
      map(([submitLoading, checkLoading]) => submitLoading || checkLoading)
    );

    this.store
      .select(fromGrow.selectGrowRiskFormDetails)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ surveyGroups, growAccount, financialDetail }) => {
        if (surveyGroups === null) {
          return;
        }
        this.growRisk = { surveyGroups, financialDetail };
        this.growAccount = growAccount;
      });

    this.store
      .select(fromAppRoot.selectCurrentAccount)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.accountID = value.id;
      });

    this.store
      .select(fromGrow.selectGrowPercentages)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((riskPercentages) => {
        if (riskPercentages === null) {
          return;
        }

        this.riskPercentages = { ...this.riskPercentages, ...riskPercentages };
        this.changeDetectorRef.detectChanges();
      });
    this.store.dispatch(GrowActions.getGrowProjects());
    this.hasSelectedProject$ = this.store.select(fromGrow.selectHasSelectedProject);
    this.store
      .select(fromGrow.selectSelectedProjects)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedProjects) => {
        this.selectedProjects = selectedProjects;
      });
  }

  onValuesChanged(data: { answers: InyovaGrowSurveyQuestion[]; availableAssets: number; initInvest: number }): void {
    this.store.dispatch(GrowActions.checkGrowRiskProfile(data));
  }

  onRiskInputError() {
    this.amplitudeService.trackEvent('Grow Risk Profile Invalid');
  }

  onConfirm() {
    if (this.formComponent) {
      this.formComponent.riskFormSubmitted = true;
      this.formComponent.onSubmit();
    }
  }

  onConfirmInvestmentAmount() {
    if (this.amountFormComponent) {
      this.amountFormComponent.submit();
    }
  }

  goToRisk() {
    this.step = 2;
  }

  onSubmitInvestmentAmount(formData) {
    this.amplitudeService.trackEvent('Grow Investment Amount Submitted');
    this.investmentPlan = { initial_investment: formData.initial, monthly_investment: formData.savings };
    this.step = 3;
  }

  onSubmit(formData) {
    this.amplitudeService.trackEvent('Grow Risk Profile Submitted');
    this.store.dispatch(GrowActions.submitGrowRisk({ data: formData, id: this.accountID }));
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getTabIcon(tab: number): string {
    if (this.step > tab) {
      return `icon-i-checkmark-circle`;
    }
    return `icon-i-step-${tab}`;
  }

  tabNavigation(tabIndex: number): void {
    this.step = tabIndex;
  }

  showInfo(item: string): void {
    this.infoList[item] = !this.infoList[item];
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: item,
      screen: 'Grow Risk',
      consultation_method: 'click'
    });
  }
}
